import { sameKidName } from "@/lib/kidFactCleanup";

/**
 * Issue #1243 — the removed-kid ledger.
 *
 * #1214 archived a removed kid's `family_context` facts, and kin-chat reads
 * with `archived = false`, so it stops seeing them. That fixed the structured
 * half and it cannot fix the other one: conversation HISTORY is sent to
 * kin-chat in the request body. Resume an old thread that names her and she is
 * back in the model's context however clean the memory tables are.
 *
 * Kasandra's decision, 2026-08-31: **exclude from model context, do not
 * destroy.** Nothing here deletes or edits a message or a conversation. The
 * ledger records the name; kin-chat is TOLD, rather than her history being
 * mangled. It is reversible: re-adding a kid of that name clears the entry.
 *
 * ── ⚠️ Why these functions do not go through `updatePreferences` ────────────
 *
 * `useUserPreferences.updatePreferences` upserts and returns `error`, with no
 * `.select()`. An RLS-refused write therefore comes back 200, `error === null`,
 * nothing to inspect. Its own header says 9 of its 19 call sites discard the
 * return value deliberately — and that is correct, because those are optimistic
 * writes with nothing on screen claiming success.
 *
 * A ledger entry is not an optimistic write. It is a CLAIM. If it silently
 * fails, Kasandra sees "Sarah removed" and Kin goes on calling Sarah her
 * daughter — the feature failing in the exact costume of success, in the one
 * feature whose whole purpose is that this not happen. That is house rule 16,
 * and it is #537's fix becoming the #545 defect.
 *
 * So every write here reads its row back and asserts the ledger actually
 * contains (or no longer contains) the name. The optimistic local state update
 * can still go through the context; the durable write cannot.
 *
 * ── ⚠️ Entries are addressed BY NAME, never by array position ───────────────
 *
 * `scheduled_tasks.config` keeps people as an array for birthday_reminder and
 * friend_checkin, addressed by index, and a concurrent save that reorders the
 * array makes an index-only write overwrite the wrong person — silently, since
 * every value involved is plausible for somebody. `src/lib/contactListEdit.ts`
 * exists because of that.
 *
 * The name IS the key here, so there is no index to get wrong. That is a
 * property to preserve, not a coincidence: `removedKidLedger.test.ts` asserts
 * that no function in this file indexes the array.
 */

/** One ledger entry. `renamed_to` is null for a removal, a name for a rename. */
/**
 * ⚠️ NO KID NAME IN A THROWN ERROR MESSAGE, EVER.
 *
 * These throws are caught by callers that report to Sentry — ProSetupFlowModal
 * routes them through reportSetupWrite -> reportError -> captureException, which
 * ships the Error's message to a third-party log. A removed child's name landing
 * there is the same harm this feature exists to prevent, arriving through the
 * back door: the ledger row is deleted with her account, a Sentry event is not.
 *
 * The user-facing toasts still name her, correctly — the caller has the name in
 * scope and she is looking at the screen she typed it into. It is only the
 * telemetry-bound message that must stay name-free. (Copilot review, #1309.)
 */
export interface RemovedKidEntry {
  name: string;
  removed_at: string;
  renamed_to: string | null;
}

/** The minimal slice of the Supabase client these helpers use. */
export interface RemovedKidClient {
  from(table: string): any;
}

/**
 * Read a stored ledger into entries, tolerating anything.
 *
 * The column is `not null default '[]'` with a CHECK that it is an array, so
 * the bad shapes below should be unreachable — but this value is read on a
 * safety path, and the cost of being wrong is Kin naming a removed child. A
 * malformed entry is dropped rather than throwing: one corrupt row must not
 * take the whole ledger down with it, which would fail OPEN.
 */
export function parseLedger(raw: unknown): RemovedKidEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: RemovedKidEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const rec = item as Record<string, unknown>;
    const name = typeof rec.name === "string" ? rec.name.trim() : "";
    if (!name) continue;
    out.push({
      name,
      removed_at: typeof rec.removed_at === "string" ? rec.removed_at : "",
      renamed_to:
        typeof rec.renamed_to === "string" && rec.renamed_to.trim().length > 0
          ? rec.renamed_to.trim()
          : null,
    });
  }
  return out;
}

/** Is this name in the ledger? Case- and whitespace-insensitive, per #1214. */
export function isRemovedKid(
  ledger: RemovedKidEntry[],
  name: string | null | undefined,
): boolean {
  return ledger.some((e) => sameKidName(e.name, name));
}

/**
 * Read the stored ledger for one user.
 *
 * ⚠️ THROWS on a read error — it does not swallow one. A read that failed is
 * not an empty ledger, and treating it as one would fail OPEN: the caller would
 * conclude nobody was removed and go on to write a ledger that silently drops
 * every existing entry. Callers here are all inside the try/catch that already
 * reports a partial removal.
 *
 * A well-formed read of a malformed VALUE is different and does return [] —
 * see `parseLedger`.
 */
export async function readRemovedKidLedger(
  client: RemovedKidClient,
  userId: string,
): Promise<RemovedKidEntry[]> {
  const { data, error } = await client
    .from("user_preferences")
    .select("removed_kids")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return parseLedger(data?.removed_kids);
}

/**
 * Write a ledger and prove it landed.
 *
 * ⚠️ `.select("removed_kids")` is not decoration. Without it a refused write is
 * indistinguishable from a successful one — see this file's header.
 */
async function writeLedger(
  client: RemovedKidClient,
  userId: string,
  next: RemovedKidEntry[],
): Promise<RemovedKidEntry[]> {
  const { data, error } = await client
    .from("user_preferences")
    .upsert(
      { user_id: userId, removed_kids: next, updated_at: new Date().toISOString() },
      { onConflict: "user_id" },
    )
    .select("removed_kids");
  if (error) throw error;
  const rows = Array.isArray(data) ? data : [];
  if (rows.length === 0) {
    // supabase-js RESOLVES on an RLS refusal: 200, error null, zero rows.
    throw new Error("Removed-kid ledger write was refused — nothing was saved.");
  }
  return parseLedger((rows[0] as Record<string, unknown>).removed_kids);
}

/**
 * Record that a kid was removed.
 *
 * Idempotent by name: removing, re-adding and removing again leaves one entry,
 * with the newer timestamp. A duplicate would render the instruction twice.
 */
export async function recordRemovedKid(
  client: RemovedKidClient,
  userId: string,
  name: string,
): Promise<RemovedKidEntry[]> {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return readRemovedKidLedger(client, userId);

  const current = await readRemovedKidLedger(client, userId);
  const next = [
    ...current.filter((e) => !sameKidName(e.name, trimmed)),
    { name: trimmed, removed_at: new Date().toISOString(), renamed_to: null },
  ];
  const landed = await writeLedger(client, userId, next);
  if (!isRemovedKid(landed, trimmed)) {
    throw new Error("Ledger write did not persist the removed name — the removal is not recorded.");
  }
  return landed;
}

/**
 * Record that a kid was renamed.
 *
 * A rename is NOT a removal — the child still exists, under a new name. The
 * instruction kin-chat renders has to say so, or Kin will tell her that her own
 * daughter is no longer part of the family. Hence `renamed_to`.
 *
 * A no-op rename (same name, different case or spacing) records nothing: there
 * is no stale name to suppress.
 */
export async function recordRenamedKid(
  client: RemovedKidClient,
  userId: string,
  oldName: string,
  newName: string,
): Promise<RemovedKidEntry[]> {
  const from = (oldName ?? "").trim();
  const to = (newName ?? "").trim();
  if (!from || !to || sameKidName(from, to)) {
    return readRemovedKidLedger(client, userId);
  }

  const current = await readRemovedKidLedger(client, userId);
  const next = [
    // The NEW name must not stay in the ledger — she exists again under it.
    ...current.filter((e) => !sameKidName(e.name, from) && !sameKidName(e.name, to)),
    { name: from, removed_at: new Date().toISOString(), renamed_to: to },
  ];
  const landed = await writeLedger(client, userId, next);
  if (!isRemovedKid(landed, from)) {
    throw new Error("Ledger write did not persist the previous name — the rename is not recorded.");
  }
  return landed;
}

/**
 * Clear a name from the ledger, because a kid of that name was added back.
 *
 * ⚠️ CALL THIS ONLY FROM AN EXPLICIT USER ACTION — My Kids, or Pro setup.
 *
 * `known_kid_names` has a third writer: `TodayTab.harvestKidsFromPlan` calls
 * `addKnownKid` for names it INFERS from an imported photo or screenshot. If
 * clearing were keyed off `known_kid_names` membership, importing a school
 * calendar that happens to name Sarah would resurrect her — the precise harm
 * #1243 exists to prevent, walking back in through a side door. Kasandra scoped
 * this feature "only on an explicit user action"; that applies to the clear as
 * much as to the write.
 */
export async function clearRemovedKid(
  client: RemovedKidClient,
  userId: string,
  name: string,
): Promise<RemovedKidEntry[]> {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return readRemovedKidLedger(client, userId);

  const current = await readRemovedKidLedger(client, userId);
  if (!isRemovedKid(current, trimmed)) return current;

  const next = current.filter((e) => !sameKidName(e.name, trimmed));
  const landed = await writeLedger(client, userId, next);
  if (isRemovedKid(landed, trimmed)) {
    throw new Error("The name is still in the ledger — the clear did not save.");
  }
  return landed;
}
